// frontend/src/components/historias/SesionesEtapaPaciente.jsx

import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Alert, useTheme, useMediaQuery,
  Collapse, Stack, Divider, TablePagination, Tooltip, FormControl,
  InputLabel, Select, Fade, LinearProgress
} from '@mui/material';
import {
 
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  LocalHospital as LocalHospitalIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarMonth as CalendarMonthIcon,
 
  Assignment as AssignmentIcon,
  Numbers as NumbersIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  PersonOutline as PersonOutlineIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import sesionEtapaService from '../../services/sesionEtapaService';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../hooks/useAuth';
import { formatDate } from '../../utils/dateUtils';

 
const MobileCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  border: '1px solid #e0e0e0',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: theme.shadows[3],
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0, 0.5, 1, 0.5),
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStyles = () => {
    switch (status) {
      case 'atendido':
        return { 
          bg: '#e8f5e9', 
          color: '#2e7d32',
          border: '1px solid #4caf50'
        };
      case 'no_atendido':
        return { 
          bg: '#ffebee', 
          color: '#c62828',
          border: '1px solid #f44336'
        };
      case 'pendiente':
        return { 
          bg: '#fff3e0', 
          color: '#e65100',
          border: '1px solid #ff9800'
        };
      default:
        return { 
          bg: '#f5f5f5', 
          color: '#616161',
          border: '1px solid #9e9e9e'
        };
    }
  };

  const styles = getStyles();
  
  return {
    backgroundColor: styles.bg,
    color: styles.color,
    border: styles.border,
    fontWeight: 600,
    fontSize: theme.breakpoints.down('sm') ? '0.7rem' : '0.75rem',
  };
});

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: '1px solid #e0e0e0',
}));

const SesionesEtapaPaciente = ({ pacienteId, historiaId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showNotification } = useNotification();
  const { user } = useAuth();
  
  // Estados
  const [sesiones, setSesiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sesionSeleccionada, setSesionSeleccionada] = useState(null);
  const [formData, setFormData] = useState({
    estado: '',
    observaciones: ''
  });
  const [guardando, setGuardando] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  // Roles permitidos para cambiar estado de atención
  const rolesPermitidos = ['Especialista', 'Interno', 'Administrador'];

 

  // Cargar sesiones del paciente
  useEffect(() => {
    cargarSesiones();
  }, [pacienteId]);

  const cargarSesiones = async () => {
  
  
  if (!pacienteId) {
    
    return;
  }
  
  try {
    setLoading(true);
    
    const response = await sesionEtapaService.getHistorialAtencionPaciente(pacienteId);
   
  
    const sesionesData = response.data?.sesiones || [];
 
    
    // Ordenar por fecha descendente (más recientes primero)
    const sesionesOrdenadas = sesionesData.sort((a, b) => 
      new Date(b.fecha_sesion) - new Date(a.fecha_sesion)
    );
    
   
    setSesiones(sesionesOrdenadas);
    
  } catch (error) {
    console.error('❌ Error al cargar sesiones:', error);
    console.error('❌ Error response:', error.response);
    console.error('❌ Error data:', error.response?.data);
    showNotification('Error al cargar las sesiones', 'error');
  } finally {
    setLoading(false);
    
  }
};

  // Obtener estadísticas
  const getEstadisticas = () => {
    const total = sesiones.length;
    const atendidas = sesiones.filter(s => s.estado_atencion === 'atendido').length;
    const noAtendidas = sesiones.filter(s => s.estado_atencion === 'no_atendido').length;
    const pendientes = sesiones.filter(s => s.estado_atencion === 'pendiente').length;
    
    return { total, atendidas, noAtendidas, pendientes };
  };

  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Abrir diálogo de edición
  const handleEditarEstado = (sesion) => {
    setSesionSeleccionada(sesion);
    setFormData({
      estado: sesion.estado_atencion || 'pendiente',
      observaciones: sesion.observaciones_atencion || ''
    });
    setDialogOpen(true);
  };

  // Guardar cambios
  const handleGuardar = async () => {
    if (!sesionSeleccionada) return;
    
    // Solo permitir cambiar si el usuario tiene rol permitido y el estado es "pendiente"
    if (!rolesPermitidos.includes(user?.rol?.nombre)) {
      showNotification('Solo especialistas, internos y administradores pueden cambiar el estado de atención', 'warning');
      return;
    }

    if (sesionSeleccionada.estado_atencion !== 'pendiente') {
      showNotification('Solo se pueden modificar sesiones pendientes', 'warning');
      return;
    }
    
    try {
      setGuardando(true);
      
      if (formData.estado === 'atendido') {
        await sesionEtapaService.marcarSesionAtendida(
          sesionSeleccionada.id, 
          formData.observaciones
        );
      } else if (formData.estado === 'no_atendido') {
        await sesionEtapaService.marcarSesionNoAtendida(
          sesionSeleccionada.id, 
          formData.observaciones
        );
      }
      
      showNotification('Estado de sesión actualizado correctamente', 'success');
      setDialogOpen(false);
      cargarSesiones();
    } catch (error) {
      console.error('Error al actualizar sesión:', error);
      showNotification(error.response?.data?.message || 'Error al actualizar el estado de la sesión', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Toggle fila expandida (móvil)
  const toggleExpandRow = (sesionId) => {
    setExpandedRows(prev => ({
      ...prev,
      [sesionId]: !prev[sesionId]
    }));
  };

  // Obtener etiqueta de estado
  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'atendido':
        return 'Atendido';
      case 'no_atendido':
        return 'No Atendido';
      case 'pendiente':
        return 'Pendiente';
      default:
        return estado;
    }
  };

  // Obtener icono de estado
  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'atendido':
        return <CheckCircleIcon fontSize="small" />;
      case 'no_atendido':
        return <CancelIcon fontSize="small" />;
      case 'pendiente':
        return <AccessTimeIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Renderizar estadísticas
  const renderEstadisticas = () => {
    const stats = getEstadisticas();
    const porcentajeAtendidas = stats.total > 0 ? (stats.atendidas / stats.total) * 100 : 0;
    
    return (
      <InfoBox>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={0.5}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                <Typography variant="body2">
                  <strong>{stats.atendidas}</strong> Atendidas
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CancelIcon sx={{ color: '#f44336', fontSize: 20 }} />
                <Typography variant="body2">
                  <strong>{stats.noAtendidas}</strong> No Atendidas
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccessTimeIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                <Typography variant="body2">
                  <strong>{stats.pendientes}</strong> Pendientes
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Porcentaje de atención: {porcentajeAtendidas.toFixed(1)}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={porcentajeAtendidas} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4caf50'
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </InfoBox>
    );
  };

  // Renderizar tabla desktop
  const renderDesktopTable = () => (
    <Fade in={!loading}>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Etapa</TableCell>
              <TableCell>Sesión #</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Especialidad</TableCell>
              <TableCell>Especialista</TableCell>
              <TableCell align="center">Estado Cita</TableCell>
              <TableCell align="center">Estado Atención</TableCell>
              <TableCell>Observaciones</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sesiones
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((sesion) => (
                <TableRow 
                  key={sesion.id} 
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                >
                  <TableCell>{sesion.etapa?.nombre || 'N/A'}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <NumbersIcon fontSize="small" color="primary" />
                      <Typography variant="body2" fontWeight="medium">
                        {sesion.numero_sesion}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarMonthIcon fontSize="small" sx={{ color: '#666' }} />
                      {formatDate(sesion.fecha_sesion)}
                    </Box>
                  </TableCell>
                  <TableCell>{sesion.especialidad?.nombre || 'N/A'}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PersonOutlineIcon fontSize="small" sx={{ color: '#666' }} />
                      <Typography variant="body2">
                        {sesion.cita?.especialista?.usuario 
                          ? `${sesion.cita.especialista.usuario.nombre} ${sesion.cita.especialista.usuario.apellidos}`
                          : 'No asignado'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {sesion.cita && (
                      <Chip 
                        label={sesion.cita.estado} 
                        size="small"
                        variant="outlined"
                        color={sesion.cita.estado === 'Realizada' ? 'success' : 'default'}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      {getEstadoIcon(sesion.estado_atencion)}
                      <StatusChip 
                        label={getEstadoLabel(sesion.estado_atencion)} 
                        size="small"
                        status={sesion.estado_atencion}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={sesion.observaciones_atencion || ''}>
                      <Typography 
                        variant="body2" 
                        noWrap 
                        sx={{ 
                          maxWidth: 200,
                          color: sesion.observaciones_atencion ? 'inherit' : '#999'
                        }}
                      >
                        {sesion.observaciones_atencion || 'Sin observaciones'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {sesion.estado_atencion === 'pendiente' && rolesPermitidos.includes(user?.rol?.nombre) ? (
                      <Tooltip title="Marcar atención">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditarEstado(sesion)}
                          color="primary"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Chip 
                        label="Cerrado" 
                        size="small" 
                        sx={{ 
                          fontSize: '0.7rem',
                          backgroundColor: '#e0e0e0',
                          color: '#666'
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sesiones.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sesiones por página"
        />
      </TableContainer>
    </Fade>
  );

  // Renderizar vista móvil
  const renderMobileView = () => (
    <Box>
      {sesiones
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((sesion) => {
          const isExpanded = expandedRows[sesion.id];
          
          return (
            <MobileCard key={sesion.id}>
              <CardContent sx={{ pb: isExpanded ? 2 : 1 }}>
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="flex-start"
                  onClick={() => toggleExpandRow(sesion.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <CalendarMonthIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight="bold">
                        Sesión #{sesion.numero_sesion}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {formatDate(sesion.fecha_sesion)}
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary">
                      {sesion.etapa?.nombre || 'Sin etapa'} • {sesion.especialidad?.nombre || 'Sin especialidad'}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <StatusChip 
                        label={getEstadoLabel(sesion.estado_atencion)} 
                        size="small"
                        status={sesion.estado_atencion}
                        icon={getEstadoIcon(sesion.estado_atencion)}
                      />
                      {sesion.cita && (
                        <Chip 
                          label={`Cita: ${sesion.cita.estado}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    {sesion.estado_atencion === 'pendiente' && rolesPermitidos.includes(user?.rol?.nombre) && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditarEstado(sesion);
                        }}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                
                <Collapse in={isExpanded}>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Especialista
                      </Typography>
                      <Typography variant="body2">
                        {sesion.cita?.especialista?.usuario 
                          ? `${sesion.cita.especialista.usuario.nombre} ${sesion.cita.especialista.usuario.apellidos}`
                          : 'No asignado'
                        }
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Observaciones
                      </Typography>
                      <Typography variant="body2">
                        {sesion.observaciones_atencion || 'Sin observaciones'}
                      </Typography>
                    </Box>
                  </Stack>
                </Collapse>
              </CardContent>
            </MobileCard>
          );
        })}
      
      <TablePagination
        component="div"
        count={sesiones.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sesiones"
        sx={{ 
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
          }
        }}
      />
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AssignmentIcon color="primary" />
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
            Sesiones de Etapa
          </Typography>
        </Box>
        
        <Chip 
          label={`${sesiones.length} sesiones`} 
          color="primary" 
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          icon={<ScheduleIcon />}
        />
      </Box>

      {/* Estadísticas */}
      {sesiones.length > 0 && renderEstadisticas()}

      {/* Contenido */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : sesiones.length === 0 ? (
        <Alert 
          severity="info"
          icon={<WarningIcon />}
          sx={{ 
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9'
          }}
        >
          No hay sesiones registradas para este paciente. Las sesiones se crearán automáticamente cuando se programen citas.
        </Alert>
      ) : (
        isMobile ? renderMobileView() : renderDesktopTable()
      )}

      {/* Diálogo de edición */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmallMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalHospitalIcon color="primary" />
            Marcar Estado de Atención
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {sesionSeleccionada && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Sesión #:</strong> {sesionSeleccionada.numero_sesion}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fecha:</strong> {formatDate(sesionSeleccionada.fecha_sesion)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Etapa:</strong> {sesionSeleccionada.etapa?.nombre}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Especialidad:</strong> {sesionSeleccionada.especialidad?.nombre}
                  </Typography>
                  {sesionSeleccionada.cita && (
                    <Typography variant="body2">
                      <strong>Estado Cita:</strong> {sesionSeleccionada.cita.estado}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Estado de Atención</InputLabel>
                <Select
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  label="Estado de Atención"
                >
                  <MenuItem value="pendiente">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTimeIcon fontSize="small" color="warning" />
                      Pendiente
                    </Box>
                  </MenuItem>
                  <MenuItem value="atendido">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckCircleIcon fontSize="small" color="success" />
                      Atendido
                    </Box>
                  </MenuItem>
                  <MenuItem value="no_atendido">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CancelIcon fontSize="small" color="error" />
                      No Atendido
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Observaciones"
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                variant="outlined"
                placeholder="Ingrese observaciones sobre la atención..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleGuardar}
            disabled={
              guardando || 
              formData.estado === 'pendiente'
            }
            startIcon={guardando && <CircularProgress size={20} />}
          >
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SesionesEtapaPaciente;